import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { AccessControlService } from "app/access-control/access-control-service";
import {
  PATIENT_BENEFITS,
  REFERRAL_PATIENT_BENEFITS,
} from "app/access-control/common-permission-asks";
import {
  PatientChartBenefits,
  ReferralPatientChartBenefits,
} from "app/access-control/permissions";
import { PermissionsResponse } from "app/access-control/permissions-response";
import { AuthService } from "app/auth/auth.service";
import {
  AlertController,
  Events,
  ModalController,
  NavParams,
} from "ionic-angular";
import moment from "moment";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { UtilityService } from "providers/utility-service/utility-service";
import { HoldBenefitPopover } from "../hold-benefit-popover/hold-benefit-popover";
import { PatientBenefitActionEvent, PatientBenefits } from "../models";
import { PatientBenefitsTableComponent } from "../patient-benefits-table/patient-benefits-table";

@Component({
  selector: "patient-benefits-crud",
  templateUrl: "patient-benefits-crud.html",
})
export class PatientBenefitsCrudComponent implements OnInit {
  @Input() roomAndBoardEnabled: boolean;
  @Input() billingRoomAndBoardEnabled: boolean;

  @Input() patientId: number;

  @Input() patientChartId: number;

  @Input() section: string;

  showForm: boolean = false;
  editMode: boolean = false;
  pageType: string = "";

  patientBenefitActionEvent: PatientBenefitActionEvent = null;

  patientBenefitsList: PatientBenefits[] = [];
  patientBenefitsClonedList: PatientBenefits[] = [];

  activeBenefitList: PatientBenefits[] = [];
  expiredBenefitList: PatientBenefits[] = [];

  primaryBenefitsList: PatientBenefits[] = [];
  secondaryBenefitsList: PatientBenefits[] = [];
  roomAndBoardBenefitsList: PatientBenefits[] = [];

  mostRecentPrimaryBenefit: PatientBenefits = null;
  mostRecentSecondaryBenefit: PatientBenefits = null;

  responseMsg: string = "";
  permissionsAccess = {} as PermissionsResponse;
  enumsReferralPatientProfile = ReferralPatientChartBenefits;
  enumsPatientProfileBenefits = PatientChartBenefits;

  constructor(
    public navParams: NavParams,

    public referralsService: ReferralsServiceProvider,
    public patientBenefitsService: PatientBenefitsService,
    private utilService: UtilityService,
    public modalController: ModalController,
    private navBarService: NavBarServiceProvider,
    private alertCtrl: AlertController,
    public events: Events,
    private authSvc: AuthService,
    private accessCntrl: AccessControlService
  ) {
    if (this.referralsService.PATIENT_STATUS) {
      this.pageType = this.referralsService.PATIENT_STATUS;
    }
    if (
      this.pageType.toLowerCase() === "referral" ||
      this.pageType.toLowerCase() === "evaluation"
    ) {
      this.accessCntrl
        .getPermission(REFERRAL_PATIENT_BENEFITS)
        .subscribe((data) => {
          this.permissionsAccess = data;
        });
    } else {
      this.accessCntrl.getPermission(PATIENT_BENEFITS).subscribe((data) => {
        this.permissionsAccess = data;
      });
    }
  }

  @ViewChild(PatientBenefitsTableComponent)
  patientBenefitsTable: PatientBenefitsTableComponent;

  ngOnInit(): void {
    if (this.section === "IntakeQueue") {
      window.sessionStorage.setItem(
        "patientChartId",
        this.patientId.toString()
      );
      window.sessionStorage.setItem(
        "externalId",
        this.patientChartId.toString()
      );
      this.retrievePatientBenefitsList();
    } else {
      if (this.navBarService.getExternalId() === undefined) {
        this.events.subscribe("setPatientChartId", () => {
          if (this.navBarService.getExternalId() !== undefined) {
            this.patientChartId = +this.navBarService.getExternalId();
            this.retrievePatientBenefitsList();
            this.events.unsubscribe("setPatientChartId");
          }
        });
      } else {
        this.patientChartId = +this.navBarService.getExternalId();
        this.retrievePatientBenefitsList();
      }
    }

    this.events.publish("checkAdmitStatus");
  }

  ionViewCanEnter() {
    this.referralsService.ACTIVE_TAB_NAME = "patient-benefits";
    return this.authSvc.authenticated();
  }

  retrievePatientBenefitsList() {
    this.responseMsg = "";
    this.utilService.increaseSpinnerCount("Loading Benefits...");
    this.patientBenefitsService
      .getPatientBenefits(this.patientChartId)
      .subscribe(
        (response) => {
          if (response != null) {
            this.formatPatientBenefitsResponse(response);
          } else {
            this.resetBenefitsList();
            this.responseMsg = "No benefits found.";
          }
          if (this.section === "IntakeQueue") {
            this.showForm = this.activeBenefitList.length == 0;
          }
          this.utilService.decreaseSpinnerCount();
        },
        (error) => {
          this.resetBenefitsList();
          this.utilService.decreaseSpinnerCount();
          this.responseMsg =
            "error while retrieving benefits, please try again!";
        }
      );
  }

  formatPatientBenefitsResponse(response: PatientBenefits[]) {
    this.resetBenefitsList();

    this.patientBenefitsClonedList = JSON.parse(JSON.stringify(response));

    this.primaryBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "PRIMARY"
    );
    if (this.primaryBenefitsList.length > 1) {
      this.primaryBenefitsList.sort(
        (a, b) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime()
      );
    }

    if (this.primaryBenefitsList.length > 0) {
      this.mostRecentPrimaryBenefit = JSON.parse(
        JSON.stringify(this.primaryBenefitsList[0])
      );
    }

    this.secondaryBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "SECONDARY"
    );
    if (this.secondaryBenefitsList.length > 1) {
      this.secondaryBenefitsList.sort(
        (a, b) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime()
      );
    }

    if (this.secondaryBenefitsList.length > 0) {
      this.mostRecentSecondaryBenefit = JSON.parse(
        JSON.stringify(this.secondaryBenefitsList[0])
      );
    }

    this.roomAndBoardBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "ROOMANDBOARD"
    );

    this.patientBenefitsList.push(...this.primaryBenefitsList);
    this.patientBenefitsList.push(...this.secondaryBenefitsList);
    this.patientBenefitsList.push(...this.roomAndBoardBenefitsList);

    this.patientBenefitsList.forEach((f) => {
      this.mapPrimacyLevelTitle(f);
      f.effectiveDate = this.formatDate(f.effectiveDate);
      if (f.expiredDate && f.expiredDate !== "-") {
        f.expiredDate = this.formatDate(f.expiredDate);
      }

      for (const key in f) {
        if (typeof f[key] === "object" && f[key] !== null) {
          for (const subKey in f[key]) {
            if (f[key][subKey] === null || f[key][subKey] === "") {
              f[key][subKey] = "-";
            }
          }
        } else {
          if (f[key] === null || f[key] === "") {
            f[key] = "-";
          }
        }
      }
    });

    this.expiredBenefitList = this.patientBenefitsList.filter(
      (f) => f.expiredDate !== "-" && moment(f.expiredDate).isBefore(Date.now())
    );
    this.activeBenefitList = this.patientBenefitsList.filter(
      (f) =>
        f.expiredDate === "-" || moment(f.expiredDate).isSameOrAfter(Date.now())
    );
  }

  resetBenefitsList() {
    this.patientBenefitsClonedList = [];
    this.patientBenefitsList = [];
    this.primaryBenefitsList = [];
    this.secondaryBenefitsList = [];

    this.mostRecentPrimaryBenefit = null;
    this.mostRecentSecondaryBenefit = null;
  }

  mapPrimacyLevelTitle(patientBenefit: PatientBenefits) {
    if (patientBenefit.payerPrimacyLevel == "PRIMARY") {
      patientBenefit.payerPrimacyLevelTitle = "Primary";
    }
    if (patientBenefit.payerPrimacyLevel == "SECONDARY") {
      patientBenefit.payerPrimacyLevelTitle = "Secondary";
    }
    if (patientBenefit.payerPrimacyLevel == "ROOMANDBOARD") {
      patientBenefit.payerPrimacyLevelTitle = "Room And Board";
    }
  }

  formatDate(date) {
    if (date !== null && date != "") {
      return moment(date).format("MM/DD/YYYY");
    } else {
      return date;
    }
  }

  cancel(event: boolean) {
    this.showForm = event;
  }

  reloadGrid(event: boolean) {
    if (event) {
      this.retrievePatientBenefitsList();
      this.events.publish("checkAdmitStatus");
    }
  }

  editPatientBenefit(event: PatientBenefitActionEvent) {
    if (event) {
      this.patientBenefitActionEvent = event;
      let benefit = this.findBenefit(event.patientBenefit.benefitsId);
      this.patientBenefitActionEvent.patientBenefit = benefit;

      if (benefit) {
        if (event.action === "copy") {
          this.editMode = false;
          this.showForm = true;

          this.sanitizeMostRecentPrimarybenefit(event.action);
          this.sanitizeMostRecentSecondaryBenefit(event.action, benefit);
        }
        if (event.action === "edit") {
          this.editMode = true;
          this.showForm = true;

          this.sanitizeMostRecentPrimarybenefit(event.action);
          this.sanitizeMostRecentSecondaryBenefit(event.action, benefit);
        }
        if (event.action === "hold" || event.action === "unhold") {
          this.showHoldBenefitPopover(this.patientBenefitActionEvent);
        }
      }
    }
  }

  addPayer() {
    this.showForm = true;
    this.editMode = false;
    this.patientBenefitActionEvent = null;

    this.sanitizeMostRecentPrimarybenefit("add");
    this.sanitizeMostRecentSecondaryBenefit("add", null);
  }

  sanitizeMostRecentPrimarybenefit(event: string) {
    if (this.mostRecentPrimaryBenefit && event === "edit") {
      this.mostRecentPrimaryBenefit.effectiveDate = moment(
        this.mostRecentPrimaryBenefit.effectiveDate
      )
        .subtract(1, "day")
        .format("MM/DD/YYYY");
    }
  }

  sanitizeMostRecentSecondaryBenefit(event: string, benefit: PatientBenefits) {
    if (this.secondaryBenefitsList.length <= 1 && event === "edit") {
      this.mostRecentSecondaryBenefit = null;
    }

    if (this.mostRecentSecondaryBenefit) {
      if (event === "edit") {
        if (benefit.benefitsId === this.mostRecentSecondaryBenefit.benefitsId) {
          this.mostRecentSecondaryBenefit.effectiveDate = moment(
            this.secondaryBenefitsList[1].expiredDate
          )
            .add(1, "day")
            .format("MM/DD/YYYY");
        }

        this.mostRecentSecondaryBenefit.effectiveDate = moment(
          this.mostRecentSecondaryBenefit.effectiveDate
        )
          .subtract(1, "day")
          .format("MM/DD/YYYY");
      } else {
        if (
          this.mostRecentSecondaryBenefit.expiredDate !== "" &&
          this.mostRecentSecondaryBenefit.expiredDate !== "-"
        ) {
          this.mostRecentSecondaryBenefit.effectiveDate =
            this.mostRecentSecondaryBenefit.expiredDate;
        }
      }
    }
  }

  showHoldBenefitPopover(event: PatientBenefitActionEvent) {
    if (event.action === "hold") {
      let modal = this.modalController.create(
        HoldBenefitPopover,
        { patientBenefit: event.patientBenefit },
        { cssClass: "hold-benefit-popover" }
      );
      modal
        .present()
        .then((res) => {})
        .catch((err) => {
          console.error(err);
        });
      modal.onDidDismiss(() => {
        this.reloadGrid(true);
      });
    } else if (event.action === "unhold") {
      event.patientBenefit.readyToBill = "No";
      this.saveBenefitOnHold(event.patientBenefit);
    }
  }

  saveBenefitOnHold(benefit: PatientBenefits) {
    const errorMsg = "Failed to save the benefit.";

    this.utilService.increaseSpinnerCount("Saving Benefit...");
    this.patientBenefitsService
      .postPatientBenefit(this.patientChartId, benefit)
      .subscribe(
        (response) => {
          if (response) {
            this.utilService.decreaseSpinnerCount();
            this.reloadGrid(true);
          } else {
            this.utilService.decreaseSpinnerCount();
            this.onFailure(errorMsg);
          }
        },
        (_err) => {
          this.utilService.decreaseSpinnerCount();
          this.onFailure(errorMsg);
        }
      );
  }

  onFailure(msg: string) {
    this.utilService.decreaseSpinnerCount();
    const alert = this.alertCtrl.create({
      title: "Error Message",
      message: msg,
      buttons: [{ text: "Cancel" }],
    });
    alert.present();
  }

  findBenefit(benefitsId: string) {
    return this.patientBenefitsClonedList.find(
      (b) => b.benefitsId === benefitsId
    );
  }

  get canAdd(): boolean {
    if (
      this.pageType.toLowerCase() === "referral" ||
      this.pageType.toLowerCase() === "evaluation"
    ) {
      return (
        this.accessCntrl.getAccessLevel(
          this.permissionsAccess,
          this.enumsReferralPatientProfile.ReferralPatientChartBenefitsAdd
        ) === 2
      );
    } else {
      return (
        this.accessCntrl.getAccessLevel(
          this.permissionsAccess,
          this.enumsPatientProfileBenefits.PatientChartBenefitsAdd
        ) === 2
      );
    }
  }

  get canEdit(): boolean {
    if (
      this.pageType.toLowerCase() === "referral" ||
      this.pageType.toLowerCase() === "evaluation"
    ) {
      return (
        this.accessCntrl.getAccessLevel(
          this.permissionsAccess,
          this.enumsReferralPatientProfile.ReferralPatientChartBenefitsEdit
        ) === 2
      );
    } else {
      return (
        this.accessCntrl.getAccessLevel(
          this.permissionsAccess,
          this.enumsPatientProfileBenefits.PatientChartBenefitsEdit
        ) === 2
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-benefits-crud/patient-benefits-crud.ts